import { createContext, useContext, type ReactNode } from 'react';

const TenantContext = createContext<string | null>(null);

/** The active organization, provided by the shell to every product screen. */
export function TenantProvider({ organizationId, children }: { organizationId: string; children: ReactNode }) {
    return <TenantContext value={organizationId}>{children}</TenantContext>;
}

/** Query key of client data: scoped to the organization, so a cache entry can never serve another one. */
export function tenantQueryKey(organizationId: string, parts: readonly unknown[]) {
    return ['tenant', organizationId, ...parts] as const;
}

export function useTenantQueryKey(...parts: unknown[]) {
    const organizationId = useContext(TenantContext);
    if (!organizationId) {
        throw new Error('useTenantQueryKey used outside of the TenantProvider of the shell.');
    }
    return tenantQueryKey(organizationId, parts);
}

/**
 * Clears every cached query when the signed-in identity (user and active organization) changes, however it
 * changes: sign-out, expiry, another tab, organization switch. Nothing of a previous context is shown again.
 */
export function createIdentityCacheGuard(cache: { clear(): void }) {
    let current: string | null | undefined;
    return (identity: string | null) => {
        if (current !== undefined && current !== identity) {
            cache.clear();
        }
        current = identity;
    };
}
